import type MarkdownIt from 'markdown-it';
import type Token from 'markdown-it/lib/token.mjs';

type StateBlock = Parameters<
  Parameters<MarkdownIt['block']['ruler']['before']>[2]
>[0];

/** Every alignment the Markdown syntax can express. `left` is the default. */
export const TEXT_ALIGN_VALUES = [
  'left',
  'center',
  'right',
  'justify',
] as const;

export type TextAlign = (typeof TEXT_ALIGN_VALUES)[number];

/**
 * An alignment that has to be stored. Left is the default and is never
 * stored: a left-aligned block is plain Markdown.
 */
export type BlockAlign = Exclude<TextAlign, 'left'>;

/** Token attribute carrying the alignment parsed from an aligned block tag. */
export const ALIGN_TOKEN_ATTR = 'data-bangle-align';

const TEXT_ALIGN_DECLARATION_RE =
  /^\s*text-align\s*:\s*(left|center|right|justify)\s*;?\s*$/iu;

/**
 * Read a `text-align` value out of a CSS `style` string. Only a style that
 * consists of exactly one `text-align` declaration is accepted, so the aligned
 * tags stay a closed, safe vocabulary: anything else returns `null`.
 */
export function parseTextAlignStyle(style: string | null): TextAlign | null {
  const value = style
    ? TEXT_ALIGN_DECLARATION_RE.exec(style)?.[1]?.toLowerCase()
    : undefined;
  return TEXT_ALIGN_VALUES.find((known) => known === value) ?? null;
}

/**
 * Narrow an untrusted node attribute to the alignment that has to be stored.
 * `left`, `null` and anything unrecognized all mean "default".
 */
export function readBlockAlign(value: unknown): BlockAlign | null {
  switch (value) {
    case 'center':
    case 'right':
    case 'justify':
      return value;
    default:
      return null;
  }
}

/** The inline `style` declaration for an alignment. */
export function textAlignStyle(align: TextAlign): string {
  return `text-align: ${align}`;
}

/** Block tags that can carry an alignment, per supported block. */
export const ALIGNED_PARAGRAPH_TAGS = ['p'] as const;
export const ALIGNED_HEADING_TAGS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
] as const;

export type AlignedBlockTag =
  | (typeof ALIGNED_PARAGRAPH_TAGS)[number]
  | (typeof ALIGNED_HEADING_TAGS)[number];

/** The opening HTML tag written for an aligned block, e.g. `<p style="text-align: center">`. */
export function serializeAlignedBlockOpen(
  tag: AlignedBlockTag,
  align: BlockAlign,
): string {
  return `<${tag} style="${textAlignStyle(align)}">`;
}

/** The closing HTML tag written for an aligned block. */
export function serializeAlignedBlockClose(tag: AlignedBlockTag): string {
  return `</${tag}>`;
}

/**
 * Read the alignment stamped by the aligned-block tokenizers on a
 * `paragraph_open` / `heading_open` token. Plain tokens read as the default.
 */
export function readAlignedBlockTokenAlign(token: Token): BlockAlign | null {
  return readBlockAlign(token.attrGet(ALIGN_TOKEN_ATTR));
}

/**
 * markdown-it block plugin for aligned paragraphs:
 *
 * ```
 * <p style="text-align: center">Centered text</p>
 * ```
 *
 * Produces ordinary `paragraph_open` / `inline` / `paragraph_close` tokens,
 * the open token carrying the alignment (see {@link readAlignedBlockTokenAlign}).
 * Raw HTML stays disabled globally; this is the only HTML it recognizes.
 */
export function alignedParagraphTokenizer(md: MarkdownIt): void {
  registerAlignedBlockRule(md, 'aligned_paragraph', ALIGNED_PARAGRAPH_TAGS);
}

/**
 * markdown-it block plugin for aligned headings, `<h1>`…`<h6>` with the same
 * safe `style="text-align: …"` form as {@link alignedParagraphTokenizer}.
 * Produces ordinary `heading_open` / `inline` / `heading_close` tokens.
 */
export function alignedHeadingTokenizer(md: MarkdownIt): void {
  registerAlignedBlockRule(md, 'aligned_heading', ALIGNED_HEADING_TAGS);
}

type OpenTag = {
  tag: AlignedBlockTag;
  align: TextAlign;
  /** Length of the opening tag, so the content can be sliced after it. */
  length: number;
};

/**
 * Match an opening tag at the very start of `text`. Only a single `style`
 * attribute holding exactly one `text-align` declaration is accepted; any
 * other attribute, style or tag declines.
 */
function matchOpenTag(
  text: string,
  tags: readonly AlignedBlockTag[],
): OpenTag | null {
  const match =
    /^<([a-z][a-z0-9]*)[ \t]+style=(?:"([^"]*)"|'([^']*)')[ \t]*>/u.exec(text);
  if (!match) {
    return null;
  }
  const tag = tags.find((known) => known === match[1]);
  const align = parseTextAlignStyle(match[2] ?? match[3] ?? null);
  if (!tag || !align) {
    return null;
  }
  return { tag, align, length: match[0].length };
}

/**
 * Whether `text` ends with the closing tag. An escaped `\</p>` is literal
 * text rather than a closing tag, so it only counts when the backslashes in
 * front of it pair up.
 */
function endsWithCloseTag(text: string, tag: AlignedBlockTag): boolean {
  const close = serializeAlignedBlockClose(tag);
  const trimmed = text.trimEnd();
  if (!trimmed.endsWith(close)) {
    return false;
  }
  const before = trimmed.slice(0, -close.length);
  const backslashes = /\\*$/u.exec(before)?.[0].length ?? 0;
  return backslashes % 2 === 0;
}

function registerAlignedBlockRule(
  md: MarkdownIt,
  ruleName: string,
  tags: readonly AlignedBlockTag[],
): void {
  md.block.ruler.before(
    'paragraph',
    ruleName,
    (state, startLine, endLine, silent) => {
      // Four or more columns of indent is an indented code block.
      if (indentOf(state, startLine) - state.blkIndent >= 4) {
        return false;
      }
      const first = lineText(state, startLine);
      if (!first.startsWith('<')) {
        return false;
      }
      const open = matchOpenTag(first, tags);
      if (!open) {
        return false;
      }

      // The block runs to the first line that ends with the closing tag. A
      // blank line first means the tag was never closed, so it stays text.
      let lastLine = -1;
      for (let line = startLine; line < endLine; line++) {
        if (line > startLine && state.isEmpty(line)) {
          break;
        }
        if (line > startLine && indentOf(state, line) < state.blkIndent) {
          break;
        }
        const text =
          line === startLine ? first.slice(open.length) : lineText(state, line);
        if (endsWithCloseTag(text, open.tag)) {
          lastLine = line;
          break;
        }
      }
      if (lastLine < 0) {
        return false;
      }
      if (silent) {
        return true;
      }

      const raw = state.getLines(
        startLine,
        lastLine + 1,
        state.blkIndent,
        false,
      );
      const content = raw
        .trimStart()
        .slice(open.length)
        .trimEnd()
        .slice(0, -serializeAlignedBlockClose(open.tag).length)
        // A tag of its own on a line leaves a newline at the edge of the
        // content, which is not text.
        .replace(/^\n+|\n+$/gu, '');

      const isHeading = open.tag !== 'p';
      const blockType = isHeading ? 'heading' : 'paragraph';
      const map: [number, number] = [startLine, lastLine + 1];

      const openToken = state.push(`${blockType}_open`, open.tag, 1);
      openToken.block = true;
      openToken.markup = isHeading ? '#'.repeat(Number(open.tag.slice(1))) : '';
      openToken.map = map;
      openToken.attrSet(ALIGN_TOKEN_ATTR, open.align);

      const inline = state.push('inline', '', 0);
      inline.content = content;
      inline.map = map;
      inline.children = [];

      const closeToken = state.push(`${blockType}_close`, open.tag, -1);
      closeToken.block = true;
      closeToken.markup = openToken.markup;

      state.line = lastLine + 1;
      return true;
    },
    { alt: ['paragraph', 'reference', 'blockquote', 'list'] },
  );
}

function indentOf(state: StateBlock, line: number): number {
  return state.sCount[line] ?? 0;
}

function lineText(state: StateBlock, line: number): string {
  const start = (state.bMarks[line] ?? 0) + (state.tShift[line] ?? 0);
  return state.src.slice(start, state.eMarks[line] ?? 0);
}
